// My Month — App Flow §3, all four states. Prompts flatten across families with family chips.
import {
  Button,
  CalendarGrid,
  Card,
  CardSkeleton,
  type DayMarker,
  EmptyState,
  HoldingCard,
  LoadingState,
  PageHeader,
  Section,
  StatusPill,
  TimelineStrip,
  todayIso,
  useToast,
} from '@roots/ui';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ContributeFlow } from '../components/contribute-flow.js';
import { EditorTools } from '../components/editor-tools.js';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { deadlineLabel, monthName, nextMonthName, occasionLine } from '../lib/format.js';

export function MyMonthScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { push } = useToast();
  const [openPrompt, setOpenPrompt] = useState<string | null>(null);
  const [calendarView, setCalendarView] = useState(
    () => localStorage.getItem('roots:timeline-view') === 'calendar',
  );

  const {
    data: families,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['families'],
    queryFn: () => api.listFamilies(),
  });

  const cycleQueries = useQueries({
    queries: (families?.families ?? []).map((family) => ({
      queryKey: ['cycle', family.id],
      queryFn: () => api.currentCycle(family.id),
    })),
  });

  const { data: occasions } = useQuery({
    queryKey: ['occasions', 'mine'],
    queryFn: () => api.myOccasions(),
  });

  const cycles = cycleQueries
    .map((q, i) => ({ family: families?.families[i], data: q.data }))
    .filter((x): x is { family: NonNullable<typeof x.family>; data: NonNullable<typeof x.data> } =>
      Boolean(x.family && x.data),
    );

  // §4.4 — dots announce "Prompt deadline on [date]", "[Name]'s birthday on [date]".
  const onDate = (iso: string) =>
    new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
    });
  const multiFamily = cycles.length > 1;
  const prefix = (name: string) => (multiFamily ? `${name}: ` : '');
  const markers: DayMarker[] = cycles.flatMap(({ family, data }) =>
    data.cycle
      ? [
          {
            date: data.cycle.cutoff.slice(0, 10),
            kind: 'deadline' as const,
            label: `${prefix(family.name)}Prompt deadline on ${onDate(data.cycle.cutoff)}`,
            target: `family-${family.id}`,
          },
          {
            date: data.cycle.drop_target.slice(0, 10),
            kind: 'drop' as const,
            label: `${prefix(family.name)}Edition drop on ${onDate(data.cycle.drop_target)}`,
            target: `family-${family.id}`,
          },
        ]
      : [],
  );
  for (const o of occasions?.occasions ?? []) {
    markers.push({
      date: o.date,
      kind: 'occasion',
      label: `${o.subject_name}’s ${o.occasion_type} on ${onDate(o.date)}`,
      target: `occasion-${o.id}`,
    });
  }

  // §3.5 — tapping a dot scrolls to the card it belongs to (smooth scroll).
  const scrollToMarker = (marker: DayMarker) => {
    if (!marker.target) return;
    document.getElementById(marker.target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const yearMonth = cycles[0]?.data.cycle?.year_month ?? new Date().toISOString().slice(0, 7);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="My Month" />
        <LoadingState onRetry={() => void refetch()}>
          <CardSkeleton />
          <CardSkeleton />
        </LoadingState>
      </div>
    );
  }

  if ((families?.families.length ?? 0) === 0) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="My Month" />
        <EmptyState
          message="You’re not in any families yet."
          actions={
            <Button onClick={() => void navigate({ to: '/families' })}>Create a family</Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex gap-6">
      <div className="flex min-w-0 flex-1 flex-col gap-8 md:max-w-[600px] lg:max-w-[560px]">
        <PageHeader title="My Month" subtitle={monthName(yearMonth)} />

        {/* Section 1 — active prompts, or the state that replaces them */}
        {cycles.map(({ family, data }) => {
          const cycle = data.cycle;
          const holding = data.my_membership.status === 'holding';
          const answered = new Map(data.my_contributions.map((c) => [c.prompt_id, c]));

          if (holding && cycle) {
            return (
              <div key={family.id} id={`family-${family.id}`} className="scroll-mt-20">
                <HoldingCard
                  nextMonthName={nextMonthName(cycle.year_month)}
                  theme={cycle.theme}
                  submittedCount={data.contribution_stats.submitted_count}
                  totalCount={data.contribution_stats.total_eligible_members}
                  occasionTeaser={
                    occasions?.occasions[0] ? occasionLine(occasions.occasions[0]) : null
                  }
                  onSetUpProfile={() => void navigate({ to: '/profile' })}
                />
              </div>
            );
          }

          // §7.2 — between cycles: the edition has dropped and next month has no theme yet.
          if (cycle?.status === 'dropped') {
            return (
              <Card
                key={family.id}
                id={`family-${family.id}`}
                className="flex scroll-mt-20 flex-col gap-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <h2 className="type-heading">{family.name}</h2>
                  <StatusPill tone="neutral">Between cycles</StatusPill>
                </div>
                <p className="type-body">
                  Next month’s theme is coming soon. Here’s what’s ahead ↓
                </p>
                <EditorTools
                  familyId={family.id}
                  familyName={family.name}
                  cycle={cycle}
                  stats={data.contribution_stats}
                  isEditor={cycle.editor_user_id === user?.id}
                />
              </Card>
            );
          }

          if (!cycle || cycle.status === 'awaiting_theme') {
            return (
              <Card
                key={family.id}
                id={`family-${family.id}`}
                className="flex scroll-mt-20 flex-col gap-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <h2 className="type-heading">{family.name}</h2>
                  <StatusPill tone="neutral">Theme coming soon</StatusPill>
                </div>
                <p className="type-body">
                  {cycle?.editor_user_id === user?.id
                    ? 'You’re this month’s editor — pick a theme whenever you’re ready.'
                    : `Your first edition drops on ${cycle ? new Date(cycle.drop_target).toLocaleDateString() : 'soon'}. ${data.editor?.name ?? 'The editor'} is picking a theme — prompts coming soon!`}
                </p>
                {cycle && (
                  <EditorTools
                    familyId={family.id}
                    familyName={family.name}
                    cycle={cycle}
                    stats={data.contribution_stats}
                    isEditor={cycle.editor_user_id === user?.id}
                  />
                )}
              </Card>
            );
          }

          if (['closed', 'compiling'].includes(cycle.status)) {
            return (
              <Card
                key={family.id}
                id={`family-${family.id}`}
                className="flex scroll-mt-20 flex-col gap-3"
              >
                <h2 className="type-heading">{family.name}</h2>
                <p className="type-body">
                  The {monthName(cycle.year_month)} edition is being put together ✨ Drop day:{' '}
                  {new Date(cycle.drop_target).toLocaleDateString()}
                </p>
                <EditorTools
                  familyId={family.id}
                  familyName={family.name}
                  cycle={cycle}
                  stats={data.contribution_stats}
                  isEditor={cycle.editor_user_id === user?.id}
                />
              </Card>
            );
          }

          const outstanding = data.prompts.filter(
            (p) => answered.get(p.id)?.status !== 'submitted',
          );

          return (
            <div key={family.id} id={`family-${family.id}`} className="scroll-mt-20">
              <Section
                title={family.name}
                action={
                  <StatusPill tone={deadlineLabel(cycle.cutoff).urgent ? 'active' : 'neutral'}>
                    {deadlineLabel(cycle.cutoff).label}
                  </StatusPill>
                }
              >
                {cycle.theme && <p className="type-display">{cycle.theme}</p>}

                {outstanding.length === 0 ? (
                  <EmptyState message="You’re all set for this month! 🎉" />
                ) : (
                  <div className="flex flex-col gap-3">
                    {data.prompts.map((prompt) => (
                      <ContributeFlow
                        key={prompt.id}
                        familyId={family.id}
                        familyName={family.name}
                        cycleId={cycle.id}
                        prompt={prompt}
                        existing={answered.get(prompt.id) ?? null}
                        deadline={cycle.cutoff}
                        expanded={openPrompt === prompt.id}
                        onToggle={() => setOpenPrompt(openPrompt === prompt.id ? null : prompt.id)}
                        onSubmitted={() => {
                          setOpenPrompt(null);
                          push('Your response is in! ✨');
                        }}
                      />
                    ))}
                  </div>
                )}

                <EditorTools
                  familyId={family.id}
                  familyName={family.name}
                  cycle={cycle}
                  stats={data.contribution_stats}
                  isEditor={cycle.editor_user_id === user?.id}
                />
              </Section>
            </div>
          );
        })}

        {/* Section 2 — This Month's Moments */}
        <Section
          title="This Month’s Moments"
          action={
            <Button variant="ghost" onClick={() => void navigate({ to: '/families' })}>
              Add occasion
            </Button>
          }
        >
          {(occasions?.occasions.length ?? 0) === 0 ? (
            <EmptyState message="No occasions this month. Add birthdays in your family settings." />
          ) : (
            <ul className="flex flex-col gap-2">
              {occasions?.occasions.slice(0, 8).map((o) => (
                <li
                  key={o.id}
                  id={`occasion-${o.id}`}
                  className="flex scroll-mt-20 items-center justify-between gap-3 rounded-[var(--radius-md)] bg-[var(--color-bg-surface)] p-3"
                >
                  <span className="type-body">{occasionLine(o)}</span>
                  <span className="type-caption">{new Date(o.date).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Section 3 — Monthly timeline. On ≥1024 the calendar grid owns the right column (§5.2 note). */}
        <div className="lg:hidden">
          <Section
            title="Monthly timeline"
            action={
              <Button
                variant="ghost"
                onClick={() => {
                  const next = !calendarView;
                  setCalendarView(next);
                  localStorage.setItem('roots:timeline-view', next ? 'calendar' : 'strip');
                }}
              >
                {calendarView ? 'Show strip' : 'Show calendar'}
              </Button>
            }
          >
            {calendarView ? (
              <CalendarGrid yearMonth={yearMonth} markers={markers} />
            ) : (
              <TimelineStrip
                yearMonth={yearMonth}
                markers={markers}
                todayIso={todayIso()}
                onSelect={scrollToMarker}
              />
            )}
          </Section>
        </div>
      </div>

      <aside className="hidden w-[340px] shrink-0 lg:block">
        <div className="sticky top-20">
          <Section title="Monthly timeline">
            <CalendarGrid yearMonth={yearMonth} markers={markers} />
          </Section>
        </div>
      </aside>
    </div>
  );
}
